import { inject, Injectable } from '@angular/core';
import { ConfigurationResponse } from '@api-response-types/configuration.response';
import { FeatureFlagId } from '@api-types/enums/feature-flag-id.enum';
import { Resolution } from '@api-types/enums/resolution.enum';
import { NgxVirtualSelectFieldOptionModel } from '@components/base-elements/virtual-select-field';
import { FeatureFlagSelectors } from '@core/store/feature-flags/feature-flags.selectors';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';

@Injectable({ providedIn: 'root' })
export class ResolutionService {
  private readonly translateService = inject(TranslateService);
  private readonly store = inject(Store);

  private readonly yearlyResolutionFlag = this.store.selectSignal(
    FeatureFlagSelectors.selectFeatureFlagById(FeatureFlagId.yearly_resolution),
  );
  private readonly weeklyResolutionFlag = this.store.selectSignal(
    FeatureFlagSelectors.selectFeatureFlagById(FeatureFlagId.weekly_resolution),
  );
  private readonly monthlyResolutionFlag = this.store.selectSignal(
    FeatureFlagSelectors.selectFeatureFlagById(
      FeatureFlagId.monthly_resolution,
    ),
  );

  public getResolutionOptionsByConfiguration(
    configuration: ConfigurationResponse,
  ): NgxVirtualSelectFieldOptionModel<Resolution>[] {
    // all available options, no logic yet
    const daily = {
      label: this.translateService.instant(
        `RESULT.SIDEBAR.RESOLUTION.${Resolution.Daily?.toUpperCase()}`,
      ),
      value: Resolution.Daily,
    };
    const weekly = {
      label: this.translateService.instant(
        `RESULT.SIDEBAR.RESOLUTION.${Resolution.Weekly?.toUpperCase()}`,
      ),
      value: Resolution.Weekly,
    };
    const monthly = {
      label: this.translateService.instant(
        `RESULT.SIDEBAR.RESOLUTION.${Resolution.Monthly?.toUpperCase()}`,
      ),
      value: Resolution.Monthly,
    };

    const yearly = {
      label: this.translateService.instant(
        `RESULT.SIDEBAR.RESOLUTION.${Resolution.Yearly?.toUpperCase()}`,
      ),
      value: Resolution.Yearly,
    };

    // TODO: Remove feature flag for Yearly resolution when highcharts is replaced and barchart bugs are fixed
    const yearlyIfEnabled = this.yearlyResolutionFlag().enabled ? [yearly] : [];
    const weeklyIfEnabled = this.weeklyResolutionFlag().enabled ? [weekly] : [];
    const monthlyIfEnabled = this.monthlyResolutionFlag().enabled
      ? [monthly]
      : [];

    // it is only possible to aggregate to a higher resolution
    // example 1: when daily is configured, we can aggregate to weekly, monthly, yearly to
    // example 2: when monthly is configured, we can aggregate to yearly too but NOT to daily or weekly
    switch (configuration.startForecastConfig.resolution) {
      case Resolution.Daily:
        return [
          daily,
          ...weeklyIfEnabled,
          ...monthlyIfEnabled,
          ...yearlyIfEnabled,
        ];
      case Resolution.Weekly:
        return [...weeklyIfEnabled, ...monthlyIfEnabled, ...yearlyIfEnabled];
      case Resolution.Monthly:
        return [...monthlyIfEnabled, ...yearlyIfEnabled];
      case Resolution.Yearly:
        return [...yearlyIfEnabled];
      default:
        return [...monthlyIfEnabled, ...yearlyIfEnabled];
    }
  }
}
